// src/components/Enquire.js
import React, { useState, useMemo, useEffect } from 'react';
import { db } from '../firebase'; // Adjust path if needed
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'; // Firestore functions
import { toast } from 'react-toastify'; // Notifications
import 'react-toastify/dist/ReactToastify.css'; // Toastify CSS
import './main.css'; // Component-specific styles

// Helper function to apply discount safely
const applyDiscount = (price, discountPercent) => {
  // Ensure valid number inputs before calculation
  if (typeof price !== 'number' || price <= 0 || typeof discountPercent !== 'number' || discountPercent < 0 || discountPercent > 100) {
    return price; // Return original price if inputs are invalid or no discount
  }
  // Calculate and return the discounted price
  return price * (1 - discountPercent / 100);
};

// Expect customerCode and customerDiscount as props
const Enquire = ({ isOpen, onClose, product, customerCode, customerDiscount = 0 }) => { // Default discount to 0 if not provided
  // --- State Management ---
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedFabric, setSelectedFabric] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(''); // e.g., '200-499', 'Sample'
  const [isSampleRequest, setIsSampleRequest] = useState(false); // Flag for sample request
  const [otherColor, setOtherColor] = useState(''); // For custom color input
  const [otherFabric, setOtherFabric] = useState(''); // For custom fabric input
  const [message, setMessage] = useState(''); // For additional notes
  const [isSubmitting, setIsSubmitting] = useState(false); // Tracks form submission status

  // --- Static Data (Memoized for performance) ---
  const colors = useMemo(() => [
    { name: 'Classic Black', hex: '#000000' },
    { name: 'Rich Brown', hex: '#8B4513' },
    { name: 'Warm Gray', hex: '#A9A9A9' },
    { name: 'Navy Blue', hex: '#000080' },
    { name: 'Forest Green', hex: '#228B22' },
    { name: 'Burgundy', hex: '#800020' }
    // Add more colors as needed
  ], []);

  const fabrics = useMemo(() => [
    { name: 'Breathable mesh', description: 'Maximum comfort for price' },
    { name: 'Premium Letherite', description: 'For that luxurious feel' }, // Corrected typo
    { name: 'Velvet', description: 'Luxurious and soft velvet material' }
    // Add more fabrics as needed
  ], []);

  // Costs associated with different fabrics
  const FABRIC_COSTS = useMemo(() => ({
    'Premium Letherite': 150,
    'Velvet': 110,
    'Breathable mesh': 85,
    'Others': 85, // Default cost for 'Others' or unspecified fabric
  }), []);

  // Threshold and percentage for potential price surcharge based on base product rate
  const SURCHARGE_THRESHOLD = 300;
  const SURCHARGE_PERCENTAGE = 0.10; // 10%

  // --- Calculated Data (Memoized for performance) ---

  // Base Quantities (Prices BEFORE discount)
  const baseQuantities = useMemo(() => {
    if (!product) return [];
    // Safely access rates, provide fallbacks
    const rate3 = typeof product.rate3 === 'number' ? product.rate3 : 0;
    const rate2 = typeof product.rate2 === 'number' ? product.rate2 : (typeof product.Rate === 'number' ? product.Rate : 0);
    const rate1 = typeof product.Rate === 'number' ? product.Rate : (typeof product.rate2 === 'number' ? product.rate2 : 0);
    // Define quantity tiers with their *original* base prices
    return [
        { range: '200-499', price: rate3 },
        { range: '499-999', price: rate2 },
        { range: '999+', price: rate1 }
    ].filter(q => q.price > 0); // Exclude tiers with zero price if desired
  }, [product]); // Dependency: product data

  // Sample Price Calculation (including discount)
  const { samplePrice, originalSamplePrice } = useMemo(() => {
    if (!product || typeof product.Rate !== 'number' || product.Rate <= 0) {
      return { samplePrice: 0, originalSamplePrice: 0 }; // Return zero if base rate is invalid
    }
    // Calculate original sample price (e.g., 2.5x base rate)
    const baseSamplePrice = product.Rate * 2.5;
    // Apply the customer's discount to the sample price
    const discountedSamplePrice = applyDiscount(baseSamplePrice, customerDiscount);
    return {
        samplePrice: discountedSamplePrice, // The price to display and charge
        originalSamplePrice: baseSamplePrice // Keep original for comparison display
    };
  }, [product, customerDiscount]); // Dependencies: product data and customer discount

  // Final Price Calculation Details (including discount)
  const calculatedPriceDetails = useMemo(() => {
    // Conditions to exit calculation: no product, no quantity selected, sample requested, or no quantity tiers defined
    if (!product || !selectedQuantity || isSampleRequest || baseQuantities.length === 0) {
      return null;
    }

    // Find the data for the selected quantity range from the *base* quantities list
    const selectedQtyData = baseQuantities.find(q => q.range === selectedQuantity);
    const originalBasePrice = selectedQtyData ? selectedQtyData.price : 0; // Original price for this tier

    // ** Apply customer discount to the base price of the selected tier **
    const discountedBasePrice = applyDiscount(originalBasePrice, customerDiscount);

    // Calculate fabric cost based on selection
    const fabricCost = selectedFabric ? (FABRIC_COSTS[selectedFabric] ?? FABRIC_COSTS['Others']) : 0;

    // Calculate potential surcharge based on the *original* base rate (Rate for 999+)
    const baseProductRate = typeof product.Rate === 'number' ? product.Rate : 0;
    const potentialSurcharge = (baseProductRate > SURCHARGE_THRESHOLD) ? (baseProductRate * SURCHARGE_PERCENTAGE) : 0;
    // Apply surcharge only if a fabric is selected
    const appliedSurcharge = selectedFabric ? potentialSurcharge : 0;

    // Cost shown for fabric line (includes fabric cost + conditional surcharge)
    const fabricDisplayCost = fabricCost + appliedSurcharge;

    // ** Calculate final total price: Discounted Base + Fabric Cost + Surcharge **
    const totalPrice = discountedBasePrice + fabricCost + appliedSurcharge;

    // Return detailed breakdown for display
    return {
      originalBasePrice: originalBasePrice, // Original price for the tier
      basePrice: discountedBasePrice,       // *Discounted* base price for the tier
      fabricName: selectedFabric,
      fabricDisplayCost: fabricDisplayCost, // Combined fabric/surcharge cost
      originalFabricCost: fabricCost,       // Base fabric cost
      surchargeApplied: appliedSurcharge,   // Surcharge amount added
      totalPrice: totalPrice,               // Final estimated total per piece
    };
  }, [product, selectedQuantity, selectedFabric, isSampleRequest, baseQuantities, FABRIC_COSTS, customerDiscount]); // Dependencies include customerDiscount

  // --- Effects ---

  // Effect to Reset Form State when modal opens or product changes
  useEffect(() => {
    if (isOpen) {
      // Reset all user selections and inputs
      setSelectedColor('');
      setSelectedFabric('');
      setSelectedQuantity('');
      setIsSampleRequest(false);
      setOtherColor('');
      setOtherFabric('');
      setMessage('');
      // Customer code and discount remain as they are passed via props
    }
  }, [isOpen, product]); // Run when modal opens or the product context changes

  // --- Event Handlers ---
  const handleColorSelect = (colorName) => {
    setSelectedColor(colorName);
    if (colorName !== 'Others') { setOtherColor(''); } // Clear custom input if standard color selected
  };

  const handleFabricSelect = (fabricName) => {
    setSelectedFabric(fabricName);
    if (fabricName !== 'Others') { setOtherFabric(''); } // Clear custom input if standard fabric selected
  };

  const handleSampleRequest = () => {
    const currentlyRequesting = !isSampleRequest;
    setIsSampleRequest(currentlyRequesting);
    // When requesting a sample, set quantity state accordingly
    if (currentlyRequesting) {
      setSelectedQuantity('Sample');
    } else { // If deselecting sample, clear quantity
      setSelectedQuantity('');
    }
  };

  const handleQuantitySelect = (quantityRange) => {
    setSelectedQuantity(quantityRange);
    // If user selects a quantity range, automatically deselect sample request mode
    if (isSampleRequest && quantityRange !== 'Sample') {
       setIsSampleRequest(false);
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value); // Update message state on textarea change
  };

  // --- Form Submission Handler ---
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent standard form submission

    // ** Input Validation **
    if (!isSampleRequest && !selectedQuantity) {
        toast.warn('Please select a quantity range or request a sample.'); return;
    }
    if (!selectedColor || (selectedColor === 'Others' && !otherColor.trim())) {
        toast.warn('Please select a color or specify your desired color.'); return;
    }
     if (!selectedFabric || (selectedFabric === 'Others' && !otherFabric.trim())) {
        toast.warn('Please select a fabric or specify your desired fabric.'); return;
    }
    // Essential Check: Ensure customer code prop is present (should always be if modal opens)
    if (!customerCode) {
        toast.error('Customer identification missing. Please re-login and try again.');
        console.error("Enquire Submit Error: customerCode prop is missing.");
        return; // Stop submission if code is missing
    }
    // ** End Validation **

    setIsSubmitting(true); // Indicate submission in progress

    // Determine final price (which already includes the discount from calculations above)
    let finalEstimatedPrice = null;
    if (isSampleRequest) {
        finalEstimatedPrice = samplePrice > 0 ? samplePrice.toFixed(2) : null;
    } else if (calculatedPriceDetails) {
        finalEstimatedPrice = calculatedPriceDetails.totalPrice.toFixed(2);
    }

    // Determine final color/fabric strings
    const finalColor = selectedColor === 'Others' ? otherColor.trim() : selectedColor;
    const finalFabric = selectedFabric === 'Others' ? otherFabric.trim() : selectedFabric;

    // --- Prepare Data Object for Firestore ---
    const enquiryData = {
      // Product Info
      productName: product?.["Product name"] || 'Unknown Product', // Use optional chaining for safety
      sku: product?.SKU || 'N/A',

      // Customer Info
      customerCode: customerCode, // Logged-in customer's code
      customerDiscountApplied: customerDiscount, // Record the discount % applied at time of enquiry

      // User Selections
      selectedQuantity: selectedQuantity, // e.g., '200-499', 'Sample'
      isSampleRequest: isSampleRequest,
      selectedColor: finalColor, // Final color choice
      selectedFabric: finalFabric, // Final fabric choice

      // Pricing Estimate (already includes discount)
      estimatedPricePerPiece: finalEstimatedPrice, // Store the calculated estimate (can be null)

      // Additional Info & Timestamp
      message: message.trim(), // User's message (trimmed)
      submittedAt: serverTimestamp(), // Use Firestore server timestamp for accuracy
      status: "New" // Initial status for tracking in backend/CRM
    };

    // console.log("Submitting Enquiry Data:", enquiryData); // Optional: for debugging

    try {
      // Add the enquiry document to the 'customerInquiries' collection
      await addDoc(collection(db, 'customerInquiries'), enquiryData);

      // Show success message to user
      toast.success(
        isSampleRequest
          ? 'Sample request submitted successfully!'
          : 'Enquiry submitted successfully! We will get back to you soon.',
        { position: "top-center" } // Configure toast position
      );

      // Reset form state (delegated to useEffect) and close the modal
      onClose();

    } catch (error) {
      console.error('Error submitting enquiry to Firestore:', error);
      toast.error('Failed to submit enquiry due to an error. Please try again.', { position: "top-center" });
    } finally {
      setIsSubmitting(false); // Re-enable submit button regardless of outcome
    }
  };
  // --- End Form Submission ---


  // --- Render Logic ---

  // Don't render the modal if isOpen prop is false
  if (!isOpen) return null;

  // Show a basic loading state if product data isn't ready yet (passed as prop)
  if (!product) {
      return (
         <div className="modal-overlay">
            <div className="modal-content">
                <p>Loading product data...</p>
                {/* Allow closing even while loading */}
                <button className="modal-close" onClick={onClose}>×</button>
            </div>
         </div>
      );
  }

  // Main modal structure
  return (
    <>
      {/* Modal Overlay (closes modal on click) */}
      <div className="modal-overlay" onClick={onClose}>
        {/* Modal Content (prevents closing when clicking inside content area) */}
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Close Button */}
          <button className="modal-close" onClick={onClose} aria-label="Close modal">×</button>

          {/* Modal Header */}
          <div className="modal-header">
            <div className="product-summary">
              <img
                src={product.image || '/placeholder.png'} // Product image or placeholder
                alt={product["Product name"] || 'Product Image'}
                className="product-thumbnail"
                onError={(e) => e.target.src='/placeholder.png'} // Fallback for broken image
              />
              <div className="product-info">
                <h2>Product Enquiry</h2>
                <p className="product-name">{product["Product name"]}</p>
                <p className="product-sku">SKU: {product.SKU || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Modal Body - Contains all interactive sections */}
          <div className="modal-body">

            {/* Quantity Selection Section */}
            <div className="quantity-section section-block">
              <h3>Select Quantity Range</h3>
              <div className="quantity-options">
                {baseQuantities.length > 0 ? baseQuantities.map((qty) => {
                   // Calculate discounted price for display in this tier
                   const discountedQtyPrice = applyDiscount(qty.price, customerDiscount);
                   return (
                      <div
                        key={qty.range} // Unique key for React list
                        className={`quantity-option ${selectedQuantity === qty.range ? 'selected' : ''}`} // Highlight if selected
                        onClick={() => handleQuantitySelect(qty.range)} // Select on click
                        role="button" // Accessibility
                        tabIndex={0} // Make focusable
                        onKeyPress={(e) => e.key === 'Enter' && handleQuantitySelect(qty.range)} // Allow selection with Enter key
                      >
                        <h4>{qty.range} pcs</h4>
                        {/* Display the calculated discounted price */}
                        <p>~ ₹{discountedQtyPrice.toFixed(2)} /pc</p>
                        {/* Optionally show the original price struck-through if a discount was applied */}
                        {customerDiscount > 0 && qty.price !== discountedQtyPrice && (
                            <span className="original-price-strike" style={{ textDecoration: 'line-through', color: '#888', marginLeft:'5px' }}> (₹{qty.price.toFixed(2)})</span>
                        )}
                      </div>
                   );
                }) : <p>Pricing information unavailable.</p> /* Message if no quantity tiers */}
              </div>
            </div>

            {/* Sample Request Section */}
            <div className="sample-section section-block">
              <h3>Need to See it First?</h3>
              <div className={`sample-option ${isSampleRequest ? 'requesting' : ''}`}>
                <div className="sample-info">
                  <h4>Get a Sample</h4>
                  <p>
                    {/* Display discounted sample price or unavailable message */}
                    {samplePrice > 0 ? (
                        <>
                          Approx. ₹{originalSamplePrice.toFixed(2)}
                         
                          
                        </>
                    ) : `Sample price unavailable`}
                  </p>
                   <p className="sample-note">Ideal for quality checks.</p>
                </div>
                {/* Button to toggle sample request state */}
                <button
                  className={`sample-btn ${isSampleRequest ? 'selected' : ''}`} // Style based on selection
                  onClick={handleSampleRequest}
                  disabled={originalSamplePrice <= 0} // Disable if base sample price is zero
                >
                  {isSampleRequest ? '✓ Sample Selected' : 'Request Sample'}
                </button>
              </div>
            </div>


            {/* Color Selection Section */}
            <div className="color-section section-block">
                <h3>Select Color</h3>
                <div className="color-swatches">
                    {/* Map through predefined colors */}
                    {colors.map((color) => (
                    <div
                        key={color.name}
                        className={`color-swatch ${selectedColor === color.name ? 'selected' : ''}`}
                        style={{ backgroundColor: color.hex }} // Set background color visually
                        onClick={() => handleColorSelect(color.name)}
                        title={color.name} // Tooltip for color name
                        role="button" tabIndex={0} aria-label={color.name}
                        onKeyPress={(e) => e.key === 'Enter' && handleColorSelect(color.name)}
                     />
                    ))}
                    {/* Button for selecting 'Other' color */}
                    <div
                        className={`color-swatch other-color ${selectedColor === 'Others' ? 'selected' : ''}`}
                        onClick={() => handleColorSelect('Others')}
                        title="Specify Other Color"
                        role="button" tabIndex={0} aria-label="Specify Other Color"
                        onKeyPress={(e) => e.key === 'Enter' && handleColorSelect('Others')}
                    >
                        <span>+</span> {/* Plus icon indicates custom input */}
                    </div>
                </div>
                {/* Input field shown only when 'Others' color is selected */}
                {selectedColor === 'Others' && (
                    <div className="other-input color-other-input">
                        <input
                            type="text"
                            placeholder="Specify your color requirement"
                            value={otherColor}
                            onChange={(e) => setOtherColor(e.target.value)}
                            className="other-color-input"
                            aria-label="Specify other color"
                        />
                    </div>
                )}
                {/* Optional link to a detailed color chart */}
                {/* <a href="#" className="section-link">Go to Color Chart →</a> */}
            </div>


             {/* Fabric Selection Section */}
            <div className="fabric-section section-block">
                <h3>Select Fabric</h3>
                <div className="fabric-options">
                     {/* Map through predefined fabrics */}
                    {fabrics.map((fabric) => (
                        <div
                            key={fabric.name}
                            className={`fabric-option ${selectedFabric === fabric.name ? 'selected' : ''}`}
                            onClick={() => handleFabricSelect(fabric.name)}
                            role="button" tabIndex={0}
                            onKeyPress={(e) => e.key === 'Enter' && handleFabricSelect(fabric.name)}
                        >
                            <h4>{fabric.name}</h4>
                            <p>{fabric.description}</p>
                        </div>
                    ))}
                    {/* Option for selecting 'Other' fabric */}
                    <div
                        className={`fabric-option other-fabric ${selectedFabric === 'Others' ? 'selected' : ''}`}
                        onClick={() => handleFabricSelect('Others')}
                        role="button" tabIndex={0}
                        onKeyPress={(e) => e.key === 'Enter' && handleFabricSelect('Others')}
                    >
                        <h4>Other Fabric</h4>
                        <p>Specify if not listed</p>
                    </div>
                </div>
                {/* Input field shown only when 'Others' fabric is selected */}
                {selectedFabric === 'Others' && (
                    <div className="other-input fabric-other-input">
                        <input
                            type="text"
                            placeholder="Specify your fabric requirement"
                            value={otherFabric}
                            onChange={(e) => setOtherFabric(e.target.value)}
                            className="other-fabric-input"
                            aria-label="Specify other fabric"
                        />
                    </div>
                )}
                 {/* Optional link to a detailed fabric guide */}
                {/* <a href="#" className="section-link">Go to Fabric Playground →</a> */}
            </div>


             {/* Final Price Estimate Display Section */}
            <div className="final-price-section section-block">
                <h2>Estimated Price</h2>
                {/* Display for Sample Price (already includes discount) */}
                {isSampleRequest && (
                    <div className="price-display sample-price-display">
                    {samplePrice > 0 ? (
                        <>
                            <span className="total-figure">₹{samplePrice.toFixed(2)}</span>
                            <span className="per-piece-label">  (Sample Price)</span>
                           
                        </>
                    ) : <p className="price-note">Sample price not available.</p> }
                     <p className="price-note">Final sample cost may vary. Excl. shipping.</p>
                    </div>
                )}

                {/* Display for Calculated Bulk Price (already includes discount) */}
                {!isSampleRequest && calculatedPriceDetails && (
                    <div className="price-display calculated-price-display">
                      {/* Main total figure */}
                      <div className="total-price-main">
                          <span className="total-figure">₹{calculatedPriceDetails.totalPrice.toFixed(2)}</span>
                          <span className="per-piece-label">(per piece estimate)</span>
                          {/* Info tooltip explaining calculation */}
                          <span
                            className="info-icon"
                            title={`Estimate includes base price for ${selectedQuantity} pcs (with your ${customerDiscount}% discount) + Selected Fabric (+ rate adjustment if applicable). Final price confirmed upon quote.`}
                            aria-label="Price calculation details"
                          > ⓘ </span>
                      </div>
                      {/* Price breakdown */}
                      <div className="breakdown-details">
                          <div className="breakdown-item">
                              {/* Base price shown is already discounted */}
                              <span className="item-label">Base Price ({selectedQuantity} pcs)</span>
                              <span className="item-value" title={`Original base: ₹${calculatedPriceDetails.originalBasePrice.toFixed(2)} | Discount: ${customerDiscount}%`}>
                                  ₹{calculatedPriceDetails.basePrice.toFixed(2)}
                              </span>
                          </div>
                          {/* Fabric cost line (includes surcharge if applicable) */}
                          {calculatedPriceDetails.fabricDisplayCost > 0 && (
                              <div className="breakdown-item">
                                  <span className="item-label">
                                      Fabric ({calculatedPriceDetails.fabricName || 'Selected'})
                                  </span>
                                  <span
                                      className="item-value"
                                      title={ `Fabric: ₹${calculatedPriceDetails.originalFabricCost.toFixed(2)} ${calculatedPriceDetails.surchargeApplied > 0 ? `| Rate Adj.: ₹${calculatedPriceDetails.surchargeApplied.toFixed(2)}` : ''}` }
                                  >
                                      + ₹{calculatedPriceDetails.fabricDisplayCost.toFixed(2)}
                                  </span>
                              </div>
                          )}
                      </div>
                      <p className="price-note">This is an estimate. Excl. GST & Shipping.</p>
                    </div>
                )}

                {/* Placeholder when no selection is made */}
                {!isSampleRequest && !calculatedPriceDetails && (
                     <div className="price-display placeholder-price-display">
                         <p className="placeholder-text">Select quantity range and options to see price estimate.</p>
                     </div>
                )}
            </div>


            {/* Simplified Enquiry Form Section */}
            <form onSubmit={handleSubmit} className="enquiry-form section-block">
                <h2>Confirm Enquiry</h2>
                {/* Display Customer Code (non-editable) */}
                <div className="form-group">
                    <label htmlFor="customerCodeDisplay" className="form-label">Customer Code:</label>
                    <input
                        type="text"
                        id="customerCodeDisplay"
                        name="customerCodeDisplay"
                        value={customerCode || 'N/A'} // Show logged-in code or fallback
                        className="form-input-disabled" // Style as disabled
                        disabled // Prevent editing
                        readOnly // Ensure non-editable
                    />
                </div>
                 
                {/* Message Textarea */}
                <div className="form-group">
                    <label htmlFor="message" className="form-label">Additional Notes (Optional):</label>
                    <textarea
                        id="message"
                        name="message"
                        placeholder="Add any specific requests, questions, or customization details here..."
                        value={message}
                        onChange={handleMessageChange}
                        rows="4"
                        className="form-textarea" // Apply specific styling
                    />
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    className="button submit-btn" // Use appropriate button class
                    // Disable button while submitting OR if essential selections for price calculation are missing (unless it's a sample request)
                    disabled={isSubmitting || (!isSampleRequest && !calculatedPriceDetails && samplePrice <= 0) }
                >
                    {/* Dynamic button text */}
                    {isSubmitting ? 'Submitting...' : (isSampleRequest ? 'Submit Sample Request' : 'Submit Enquiry')}
                </button>
            </form>
            {/* --- END: Simplified Enquiry Form Section --- */}

          </div> {/* End modal-body */}
        </div> {/* End modal-content */}
      </div> {/* End modal-overlay */}
    </>
  );
};

export default Enquire;